import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { airtableService } from '../services/airtable';
import { formatToLocalTime, formatToLocalDate } from '../utils/timeUtils';
import { CATEGORIES, SCORE_LEVELS } from '../data/rubricData';

const SessionSummary = ({ user, currentSession }) => {
  const { sessionId: routeSessionId } = useParams();
  const [evaluations, setEvaluations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [displaySession, setDisplaySession] = useState(currentSession);
  const isHistoricalView = Boolean(routeSessionId) && routeSessionId !== currentSession?.sessionId;

  useEffect(() => {
    loadSessionData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSession, routeSessionId]);

  const loadSessionData = async () => {
    const targetSessionId = routeSessionId || currentSession?.sessionId;
    if (!targetSessionId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      console.log('Loading session summary for:', targetSessionId);

      if (routeSessionId && routeSessionId !== currentSession?.sessionId) {
        const sessionRecord = await airtableService.getSessionBySessionId(routeSessionId);
        if (sessionRecord) {
          setDisplaySession({
            sessionId: sessionRecord.fields['Session ID'],
            clinicCode: sessionRecord.fields['Clinic Code'],
            accessCode: sessionRecord.fields['Access Code'],
            doctorName: sessionRecord.fields['Doctor Name'],
            startTime: sessionRecord.fields['Start Time'],
            endTime: sessionRecord.fields['End Time'],
            status: sessionRecord.fields['Status']
          });
        }
      } else {
        setDisplaySession(currentSession);
      }

      const evaluationRecords = await airtableService.getSessionEvaluations(targetSessionId);
      console.log('Found evaluations:', evaluationRecords.length);
      setEvaluations(evaluationRecords);
    } catch (error) {
      console.error('Error loading session data:', error);
      setEvaluations([]);
    } finally {
      setLoading(false);
    }
  };

  const isSkipped = (evaluation) => {
    return Boolean(evaluation.fields['Evaluation Skipped']);
  };

  const hasRubricScores = (evaluation) => {
    return !isSkipped(evaluation) && evaluation.fields['Professionalism Score'] != null;
  };

  const getSessionStats = () => {
    const totalStudents = evaluations.length;
    const avgResponseTime = evaluations.length > 0 ?
      evaluations.reduce((acc, e) => acc + (e.fields['Response Time Minutes'] || 0), 0) / evaluations.length : 0;

    const requeued = evaluations.filter(e => e.fields['Requeue Student']).length;

    // Rubric-based averages (only from evaluations that have scores)
    const rubricEvals = evaluations.filter(hasRubricScores);
    const rubricCount = rubricEvals.length;

    const avgScores = {};
    CATEGORIES.forEach(cat => {
      if (rubricCount > 0) {
        const sum = rubricEvals.reduce((acc, e) => acc + (e.fields[cat.fieldName] || 0), 0);
        avgScores[cat.key] = sum / rubricCount;
      } else {
        avgScores[cat.key] = 0;
      }
    });

    const overallAvg = rubricCount > 0 ?
      CATEGORIES.reduce((acc, cat) => acc + avgScores[cat.key], 0) / CATEGORIES.length : 0;

    return {
      totalStudents,
      avgResponseTime: Math.round(avgResponseTime),
      requeued,
      avgScores,
      overallAvg,
      rubricCount
    };
  };

  const getScoreColor = (score) => {
    if (score >= 3.5) return '#4CAF50';
    if (score >= 2.5) return '#2196F3';
    if (score >= 1.5) return '#FF9800';
    return '#f44336';
  };

  const getScoreLabel = (score) => {
    const rounded = Math.round(score);
    const level = SCORE_LEVELS.find(l => l.score === rounded);
    return level ? level.label : '';
  };

  const formatTime = (timeString) => {
    return formatToLocalTime(timeString);
  };

  const formatDate = (timeString) => {
    return formatToLocalDate(timeString);
  };

  const getUniqueStudents = () => {
    const students = {};
    evaluations.forEach(evaluation => {
      const name = evaluation.fields['Student Name'];
      if (!students[name]) {
        students[name] = [];
      }
      students[name].push(evaluation);
    });
    return students;
  };

  const renderScoreBadge = (score) => {
    if (score == null) return <span style={{ color: 'var(--text-light)', fontSize: '14px' }}>N/A</span>;
    const color = getScoreColor(score);
    return (
      <span style={{
        display: 'inline-block',
        padding: '2px 10px',
        borderRadius: '12px',
        fontSize: '14px',
        fontWeight: 'bold',
        backgroundColor: color,
        color: 'white',
        minWidth: '30px',
        textAlign: 'center'
      }}>
        {typeof score === 'number' && !Number.isInteger(score) ? score.toFixed(1) : score}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="container">
        <div className="card">
          <p>Loading session summary...</p>
        </div>
      </div>
    );
  }

  if (!displaySession) {
    return (
      <div className="container">
        <div className="header">
          <h1>Session Summary</h1>
          <p>No session found</p>
        </div>
        <div className="card">
          <Link to="/dr-grant/dashboard" className="btn btn-primary">
            Back to Dashboard
          </Link>
        </div>
      </div>
    );
  }

  const stats = getSessionStats();
  const studentGroups = getUniqueStudents();

  return (
    <div className="container">
      <div className="header">
        <h1>{isHistoricalView ? 'Past Session' : 'Session Summary'}</h1>
        <p>{displaySession.clinicCode} — {formatDate(displaySession.startTime)}</p>
        {displaySession.endTime && (
          <p style={{ color: 'var(--text-light)', fontSize: '14px', margin: 0 }}>
            Ended {formatTime(displaySession.endTime)}
          </p>
        )}
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '16px',
        marginBottom: '24px'
      }}>
        <div className="card" style={{ textAlign: 'center' }}>
          <h3 style={{ color: 'var(--primary-blue)', fontSize: '2em', margin: '0 0 8px 0' }}>
            {stats.totalStudents}
          </h3>
          <p style={{ color: 'var(--text-dark)', margin: 0 }}>Total Evaluations</p>
        </div>

        <div className="card" style={{ textAlign: 'center' }}>
          <h3 style={{ color: 'var(--coral)', fontSize: '2em', margin: '0 0 8px 0' }}>
            {stats.avgResponseTime}m
          </h3>
          <p style={{ color: 'var(--text-dark)', margin: 0 }}>Avg Response Time</p>
        </div>

        <div className="card" style={{ textAlign: 'center' }}>
          <h3 style={{ color: getScoreColor(stats.overallAvg), fontSize: '2em', margin: '0 0 8px 0' }}>
            {stats.rubricCount > 0 ? stats.overallAvg.toFixed(1) : '-'}
          </h3>
          <p style={{ color: 'var(--text-dark)', margin: 0 }}>Avg Overall Score</p>
        </div>

        <div className="card" style={{ textAlign: 'center' }}>
          <h3 style={{ color: 'var(--soft-lavender)', fontSize: '2em', margin: '0 0 8px 0' }}>
            {stats.requeued}
          </h3>
          <p style={{ color: 'var(--text-dark)', margin: 0 }}>Requeued</p>
        </div>
      </div>

      {stats.rubricCount > 0 && (
        <div className="card">
          <h2 style={{ color: 'var(--text-dark)', marginBottom: '16px' }}>
            Rubric Score Averages
          </h2>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '16px',
            marginBottom: '16px'
          }}>
            {CATEGORIES.map(cat => {
              const avg = stats.avgScores[cat.key];
              const percentage = (avg / 4) * 100;
              const color = getScoreColor(avg);

              return (
                <div key={cat.key} style={{
                  padding: '16px',
                  backgroundColor: 'var(--warm-cream)',
                  borderRadius: '8px'
                }}>
                  <h4 style={{ color: 'var(--text-dark)', marginBottom: '8px' }}>
                    {cat.shortLabel}
                  </h4>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    marginBottom: '4px'
                  }}>
                    <div style={{
                      flex: 1,
                      height: '8px',
                      backgroundColor: '#e1e8ed',
                      borderRadius: '4px',
                      overflow: 'hidden'
                    }}>
                      <div style={{
                        width: `${percentage}%`,
                        height: '100%',
                        backgroundColor: color,
                        transition: 'width 0.3s ease'
                      }}></div>
                    </div>
                    {renderScoreBadge(avg)}
                  </div>
                  <p style={{ color: 'var(--text-light)', fontSize: '13px', margin: '4px 0 0 0' }}>
                    {getScoreLabel(avg)} ({stats.rubricCount} evaluation{stats.rubricCount !== 1 ? 's' : ''})
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="card">
        <h2 style={{ color: 'var(--text-dark)', marginBottom: '16px' }}>
          Student Details
        </h2>

        {Object.entries(studentGroups).length === 0 ? (
          <p style={{ color: 'var(--text-light)', textAlign: 'center', padding: '40px' }}>
            No student evaluations found for this session.
          </p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {Object.entries(studentGroups).map(([studentName, studentEvals]) => (
              <div
                key={studentName}
                style={{
                  border: '2px solid var(--light-blue)',
                  borderRadius: '8px',
                  overflow: 'hidden'
                }}
              >
                <div
                  style={{
                    padding: '16px',
                    backgroundColor: selectedStudent === studentName ? 'var(--warm-cream)' : 'var(--light-blue)',
                    cursor: 'pointer',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: '12px'
                  }}
                  onClick={() => setSelectedStudent(selectedStudent === studentName ? null : studentName)}
                >
                  <div>
                    <h4 style={{ color: 'var(--text-dark)', margin: '0 0 4px 0' }}>
                      {studentName}
                      {studentEvals[0]?.fields['Year Level'] && (
                        <span style={{
                          marginLeft: '8px',
                          padding: '2px 8px',
                          fontSize: '11px',
                          fontWeight: 'bold',
                          borderRadius: '10px',
                          backgroundColor: studentEvals[0].fields['Year Level'] === 'D4' ? '#1976D2' : '#7B1FA2',
                          color: 'white'
                        }}>
                          {studentEvals[0].fields['Year Level']}
                        </span>
                      )}
                    </h4>
                    <p style={{ color: 'var(--text-light)', margin: 0, fontSize: '14px' }}>
                      {studentEvals.length} evaluation{studentEvals.length !== 1 ? 's' : ''}
                    </p>
                  </div>

                  <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <span style={{
                      padding: '4px 8px',
                      borderRadius: '12px',
                      fontSize: '12px',
                      backgroundColor: 'white',
                      color: 'var(--text-dark)'
                    }}>
                      Avg: {Math.round(studentEvals.reduce((acc, e) => acc + (e.fields['Response Time Minutes'] || 0), 0) / studentEvals.length)}m
                    </span>
                    <span style={{ color: 'var(--text-dark)' }}>
                      {selectedStudent === studentName ? '▼' : '▶'}
                    </span>
                  </div>
                </div>

                {selectedStudent === studentName && (
                  <div style={{ padding: '16px', backgroundColor: 'white' }}>
                    {studentEvals.map((evaluation, index) => (
                      <div
                        key={evaluation.id}
                        style={{
                          padding: '12px',
                          marginBottom: index < studentEvals.length - 1 ? '12px' : 0,
                          backgroundColor: 'var(--warm-cream)',
                          borderRadius: '6px'
                        }}
                      >
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          marginBottom: '8px',
                          flexWrap: 'wrap',
                          gap: '8px'
                        }}>
                          <strong style={{ color: 'var(--text-dark)' }}>
                            Evaluation #{index + 1}
                          </strong>
                          <span style={{ color: 'var(--text-light)', fontSize: '14px' }}>
                            {formatTime(evaluation.fields['Evaluation Time'])} | {evaluation.fields['Response Time Minutes']}m wait
                          </span>
                        </div>

                        {isSkipped(evaluation) ? (
                          <div style={{
                            padding: '8px 12px',
                            marginBottom: '8px',
                            backgroundColor: 'white',
                            borderLeft: '4px solid var(--muted-sage)',
                            borderRadius: '4px',
                            fontSize: '14px',
                            color: 'var(--text-dark)'
                          }}>
                            <strong style={{ color: 'var(--muted-sage)' }}>No review needed</strong>
                            {' '}— not counted toward rubric averages.
                          </div>
                        ) : hasRubricScores(evaluation) ? (
                          <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                            gap: '8px',
                            marginBottom: '8px'
                          }}>
                            {CATEGORIES.map(cat => (
                              <span key={cat.key} style={{ fontSize: '14px' }}>
                                {cat.shortLabel}: {renderScoreBadge(evaluation.fields[cat.fieldName])}
                              </span>
                            ))}
                            <span style={{ fontSize: '14px' }}>
                              Requeued: <strong style={{ color: evaluation.fields['Requeue Student'] ? 'var(--coral)' : 'var(--muted-sage)' }}>
                                {evaluation.fields['Requeue Student'] ? 'Yes' : 'No'}
                              </strong>
                            </span>
                          </div>
                        ) : (
                          <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                            gap: '8px',
                            marginBottom: '8px'
                          }}>
                            <span style={{ fontSize: '14px' }}>
                              Prepared: <strong style={{ color: evaluation.fields['Was Prepared'] ? 'var(--muted-sage)' : 'var(--coral)' }}>
                                {evaluation.fields['Was Prepared'] ? 'Yes' : 'No'}
                              </strong>
                            </span>
                            <span style={{ fontSize: '14px' }}>
                              Professional: <strong style={{ color: evaluation.fields['Was Professional'] ? 'var(--muted-sage)' : 'var(--coral)' }}>
                                {evaluation.fields['Was Professional'] ? 'Yes' : 'No'}
                              </strong>
                            </span>
                            <span style={{ fontSize: '14px' }}>
                              Missed Findings: <strong style={{ color: evaluation.fields['Missed Findings'] ? 'var(--coral)' : 'var(--muted-sage)' }}>
                                {evaluation.fields['Missed Findings'] ? 'Yes' : 'No'}
                              </strong>
                            </span>
                            <span style={{ fontSize: '14px' }}>
                              Requeued: <strong style={{ color: evaluation.fields['Requeue Student'] ? 'var(--coral)' : 'var(--muted-sage)' }}>
                                {evaluation.fields['Requeue Student'] ? 'Yes' : 'No'}
                              </strong>
                            </span>
                          </div>
                        )}

                        {evaluation.fields['Notes'] && (
                          <div style={{
                            padding: '8px',
                            backgroundColor: 'white',
                            borderRadius: '4px',
                            fontSize: '14px',
                            color: 'var(--text-dark)'
                          }}>
                            <strong>Notes:</strong> {evaluation.fields['Notes']}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <div style={{ textAlign: 'center', marginTop: '24px', display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
        {isHistoricalView && (
          <Link to="/dr-grant/history" className="btn btn-secondary">
            Back to Past Sessions
          </Link>
        )}
        <Link to="/dr-grant/dashboard" className="btn btn-primary">
          Back to Dashboard
        </Link>
      </div>
    </div>
  );
};

export default SessionSummary;
